import {useCallback, useRef, useState} from 'react';

import {
    IRedirectPaymentMethodManager,
    PaymentMethodInfo,
    PaymentMethodType,
    PrimerHeadlessCheckout,
} from '@primer-io/checkout-web';
import {useDispatch} from 'react-redux';

import {PAYMENTS} from 'desktop/pages/checkout/constants/paymentStep';
import {useConfigureAndStartPrimer} from 'desktop/pages/checkout/hooks/primer/useConfigureAndStartPrimer';
import {setOptionPaymentMethod} from 'desktop/pages/checkout/store/reducers/analytics/analytics';
import {sendPayerInfoToSaga} from 'desktop/pages/checkout/store/reducers/paymentStep/payerInfo/payerInfo';

import {usePaymentCreationHandlers} from './usePaymentCreationHandlers';

export type PrimerRedirectableConfigureType = {
    externalMethodType: 'BRAINTREE_VENMO';
    internalMethodType: PAYMENTS;
};
/**
 * Hook for primer for redirectable payments
 */
export const usePrimerForRedirectablePayments = ({
    externalMethodType,
    internalMethodType,
}: PrimerRedirectableConfigureType) => {
    const dispatch = useDispatch();
    const primerRef = useRef<PrimerHeadlessCheckout | null>(null);
    const [isReady, setIsReady] = useState(false);
    const [manager, setManager] = useState<IRedirectPaymentMethodManager | null>(null);

    const onAvailablePaymentMethodsLoad = useCallback(
        async (paymentMethods: PaymentMethodInfo[]) => {
            if (
                !primerRef.current ||
                !paymentMethods.find(
                    method => method.type === (externalMethodType as PaymentMethodType),
                )
            )
                return;

            const paymentMethodManager = await primerRef.current.createPaymentMethodManager(
                externalMethodType as PaymentMethodType,
            );
            setManager(paymentMethodManager);
        },
        [externalMethodType],
    );

    const {onBeforePaymentCreate} = usePaymentCreationHandlers(
        PAYMENTS.METHOD_PRIMER_VENMO,
        primerRef,
    );

    useConfigureAndStartPrimer({
        onAvailablePaymentMethodsLoad,
        onBeforePaymentCreate,
        onPrimerHeadlessReady: primerHeadless => {
            primerRef.current = primerHeadless;
        },
        onReady: () => setIsReady(true),
        runSavePayment: ({orderId, token}) => {
            dispatch(
                sendPayerInfoToSaga({
                    method: internalMethodType,
                    orderIncrementId: orderId,
                    token,
                }),
            );
        },
        setOptionPayment: () => dispatch(setOptionPaymentMethod()),
        skipVaultManager: true,
    });

    return {
        isAvailable: Boolean(manager),
        isReady,
        manager,
    };
};
